import { useEffect, useState } from 'react'
import { LandmarkIcon } from 'lucide-react'

import { apiClient } from '@/api/client'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'

export interface BankCorroborationPeriod {
  period_label: string
  pl_revenue: number
  bank_deposits: number
  gap_amount: number
  gap_pct: number
  score: 'PASS' | 'REVIEW' | 'FLAG'
  note: string
}

export interface BankCorroborationCompleteness {
  status: 'COMPLETE' | 'INCOMPLETE' | 'UNVERIFIED'
  declared: number | null
  uploaded: number
  missing_count: number
  pl_institutions_detected: string[]
  pl_institutions_uploaded: string[]
  unmatched_institutions: string[]
  narrative: string
}

export interface BankCorroborationData {
  deal_id: string
  overall_score: 'PASS' | 'REVIEW' | 'FLAG' | null
  accounts_reconciled: string[]
  missing_accounts: string[]
  caveat: string | null
  periods: BankCorroborationPeriod[]
  note: string
  reporting_basis?: 'cash' | 'accrual'
  basis_assumed?: boolean
  // FIELDS-1: the API has always called this account_completeness. This card
  // read `completeness`, so the block below had never once rendered — and until
  // FIELDS-1 the handler never called check_account_completeness() either, so
  // there was nothing to render anyway.
  account_completeness?: BankCorroborationCompleteness | null
}

interface Props {
  dealId: string
  /** Bumped by the proof-of-cash form after a save, to re-read the score. */
  refreshKey?: number
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`
}

/** Gap % as a share of P&L revenue. With no revenue there is no denominator, so
 *  there is no percentage — the server sends a sentinel (see ZERO_REVENUE_GAP_PCT
 *  in core/qoe/bank_corroboration.py) purely because the field is non-nullable.
 *  Rendering it would print a precise-looking "-100.0%" that was never measured.
 *  The FLAG badge and the row note carry the finding instead. */
function fmtGapPct(p: BankCorroborationPeriod): string {
  return p.pl_revenue === 0 ? 'n/a' : fmtPct(p.gap_pct)
}

const SCORE_STYLES: Record<string, string> = {
  PASS: 'bg-green-100 text-green-800',
  REVIEW: 'bg-amber-100 text-amber-800',
  FLAG: 'bg-red-100 text-red-800',
}

function ScoreBadge({ score }: { score: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SCORE_STYLES[score] ?? 'bg-muted text-muted-foreground'}`}>
      {score}
    </span>
  )
}

/** Declared-vs-uploaded and the P&L institution cross-reference. Rendered in the
 *  pending branch too: "the seller declared three accounts and sent none" is a
 *  finding the analyst needs before any period can be scored, not an empty state. */
function CompletenessBlock({
  completeness, missingAccounts,
}: {
  completeness?: BankCorroborationCompleteness | null
  missingAccounts: string[]
}) {
  if (!completeness && missingAccounts.length === 0) return null
  return (
    <div className="border-t pt-2">
      {completeness && (
        <>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-muted-foreground">Account Completeness</span>
            <ScoreBadge score={completeness.status} />
          </div>
          <p className="text-xs text-muted-foreground">
            {completeness.narrative}
            {completeness.declared != null && (
              <>
                {' '}
                (Declared: {completeness.declared}, Uploaded: {completeness.uploaded})
              </>
            )}
          </p>
          {completeness.unmatched_institutions.length > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded p-1 mt-1">
              P&L shows expenses from: {completeness.unmatched_institutions.join(', ')} — not found in uploaded statements
            </p>
          )}
        </>
      )}
      {missingAccounts.length > 0 && (
        <ul className="mt-1 list-disc pl-4 text-xs text-amber-700">
          {missingAccounts.map(gap => <li key={gap}>{gap}</li>)}
        </ul>
      )}
    </div>
  )
}

export function BankCorroborationCard({ dealId, refreshKey = 0 }: Props) {
  const [data, setData] = useState<BankCorroborationData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get<BankCorroborationData>(`/qoe/deals/${dealId}/bank-corroboration`)
        if (!cancelled) setData(res.data)
      } catch {
        if (!cancelled) setError('Failed to load bank corroboration analysis')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [dealId, refreshKey])

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (!data || data.overall_score === null || data.periods.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">
          {data?.note ?? 'Bank corroboration analysis not yet available for this deal.'}
        </p>
        <CompletenessBlock
          completeness={data?.account_completeness}
          missingAccounts={data?.missing_accounts ?? []}
        />
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <LandmarkIcon className="h-4 w-4 text-brand-primary" />
        <span className="font-medium text-sm text-brand-text">
          Bank Statement / P&amp;L Revenue Corroboration
        </span>
        <ScoreBadge score={data.overall_score} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 pr-3 font-normal">Period</th>
              <th className="py-1 pr-3 font-normal text-right">P&amp;L Revenue</th>
              <th className="py-1 pr-3 font-normal text-right">Bank Deposits</th>
              <th className="py-1 pr-3 font-normal text-right">Gap $</th>
              <th className="py-1 pr-3 font-normal text-right">Gap %</th>
              <th className="py-1 pr-3 font-normal">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.periods.map(p => (
              <tr key={p.period_label} className="border-b last:border-0" title={p.note}>
                <td className="py-1.5 pr-3">{p.period_label}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{fmtUsd(p.pl_revenue)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{fmtUsd(p.bank_deposits)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{fmtUsd(p.gap_amount)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{fmtGapPct(p)}</td>
                <td className="py-1.5 pr-3">
                  <ScoreBadge score={p.score} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Accounts reconciled: {data.accounts_reconciled.length > 0 ? data.accounts_reconciled.join(', ') : 'No bank statements provided'}
      </p>

      <CompletenessBlock
        completeness={data.account_completeness}
        missingAccounts={data.missing_accounts}
      />

      {data.basis_assumed && data.reporting_basis && (
        <p className="text-xs text-muted-foreground">
          Basis assumed: {data.reporting_basis}. Record the deal&apos;s reporting basis
          above so the deposit-gap thresholds match it.
        </p>
      )}

      {data.caveat && (
        <p className="text-xs text-muted-foreground italic">{data.caveat}</p>
      )}
    </div>
  )
}

export default BankCorroborationCard
